import type { BausparsummeInference } from "../shared/inferBausparsumme";
import type { Transaction } from "../shared/types";
import { parseText } from "../calc/calculate";
import { MAX_PDF_BYTES, MAX_PARSE_TEXT_CHARS } from "../calc/schemas";

/** Group pdf.js text items by Y position into lines (like pdfplumber extract_text). */
function pageTextAsLines(textContent: { items: unknown[] }): string {
  const Y_TOL = 4;
  const items = textContent.items
    .filter((item): item is { str: string; transform: number[] } => {
      return (
        typeof item === "object" &&
        item !== null &&
        "str" in item &&
        typeof (item as { str: unknown }).str === "string" &&
        (item as { str: string }).str.trim().length > 0 &&
        "transform" in item
      );
    })
    .map((item) => ({
      str: item.str,
      x: item.transform[4],
      y: item.transform[5],
    }));

  items.sort((a, b) => {
    const dy = b.y - a.y;
    if (Math.abs(dy) > Y_TOL) return dy;
    return a.x - b.x;
  });

  const lines: string[] = [];
  let parts: string[] = [];
  let curY: number | null = null;

  for (const item of items) {
    if (curY !== null && Math.abs(item.y - curY) > Y_TOL) {
      lines.push(parts.join(" "));
      parts = [];
    }
    parts.push(item.str);
    curY = item.y;
  }
  if (parts.length) lines.push(parts.join(" "));

  return lines.join("\n");
}

async function loadPdfJs() {
  const pdfjsLib = await import("pdfjs-dist");
  const pdfWorkerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
  return pdfjsLib;
}

export async function extractPdfText(file: File): Promise<string> {
  if (file.size > MAX_PDF_BYTES) {
    throw new Error("PDF_TOO_LARGE");
  }

  try {
    const pdfjsLib = await loadPdfJs();
    const data = new Uint8Array(await file.arrayBuffer());
    const pdf = await pdfjsLib.getDocument({ data }).promise;
    const parts: string[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      parts.push(pageTextAsLines(content));
    }
    const text = parts.join("\n");
    if (text.length > MAX_PARSE_TEXT_CHARS) {
      throw new Error("TEXT_TOO_LONG");
    }
    return text;
  } catch (error) {
    if (error instanceof Error && ["PDF_TOO_LARGE", "TEXT_TOO_LONG"].includes(error.message)) {
      throw error;
    }
    const message = error instanceof Error ? error.message : String(error);
    if (/password|encrypted/i.test(message)) {
      throw new Error("PDF_PASSWORD");
    }
    throw new Error("PDF_UNSUPPORTED");
  }
}

export async function importPdfFile(file: File): Promise<{
  transactions: Transaction[];
  earliest: string;
  latest: string;
} & BausparsummeInference> {
  const text = await extractPdfText(file);
  return parseText(text);
}
